import { useCallback, useLayoutEffect, useRef } from 'react';

import { useBreakpoints } from '@flipdish/ui-library/hooks/useBreakpoints';
import { useSidePanel } from '@flipdish/ui-library/hooks/useSidePanel';

/** Every content the shell's one side panel can show. Notifications are not wired yet. */
export type PortalPanel = 'assistant' | 'organisation' | 'switch-organisation';

type Options = {
  /** False while the assistant is gated off: a remembered assistant must not reopen meanwhile. */
  assistantEnabled: boolean;
};

/**
 * Holds which content the shell's one side panel shows and exposes one named handler per header
 * control, after the design system's Portal Shell blueprint. The avatar's two steps count as one
 * open state, so pressing it closes the panel from either step. Below `lg` the panel covers the
 * page, so a change made behind it closes it too.
 *
 * @param options.assistantEnabled Whether the assistant may show; the memory is kept while it is off.
 * @returns The open and shown panel, the panel's open state and the handlers the header and panels bind to.
 */
export const usePortalPanels = ({ assistantEnabled }: Options) => {
  // One side panel whose content switches, so two can never show together. The assistant reopens on the next visit.
  const sidePanel = useSidePanel<PortalPanel>({ persist: ['assistant'] });
  // A remembered assistant must not reopen while the assistant is gated off (flag off, not yet
  // resolved, or onboarding waiting view). The memory is kept, so it reopens once the gate clears.
  const openPanel = sidePanel.active === 'assistant' && !assistantEnabled ? null : sidePanel.active;

  // The content stays mounted through the close animation, so focus is still inside the panel and returns to the opener.
  const lastPanel = useRef<PortalPanel | null>(null);
  if (openPanel) lastPanel.current = openPanel;
  const shownPanel = openPanel ?? lastPanel.current;

  const isOrganisationOpen = openPanel === 'organisation' || openPanel === 'switch-organisation';
  // Read by callbacks that fire after an async switch, where the closure's `openPanel` may be stale.
  // Written after commit, so a render React discards never reaches the callbacks.
  const openPanelRef = useRef<PortalPanel | null>(openPanel);
  useLayoutEffect(() => {
    openPanelRef.current = openPanel;
  }, [openPanel]);

  const { open, toggle, close, onOpenChange } = sidePanel;
  // Pressing the open panel's button closes it. Pressing another one switches the content.
  const toggleAssistant = useCallback(() => toggle('assistant'), [toggle]);
  const openAssistant = useCallback(() => open('assistant'), [open]);
  const toggleOrganisation = useCallback(
    () => (isOrganisationOpen ? close() : open('organisation')),
    [isOrganisationOpen, close, open]
  );
  // The avatar's panel is a two-step flow in the same panel: the summary's footer opens the switcher, whose back button returns.
  const showSwitcher = useCallback(() => open('switch-organisation'), [open]);
  // Only returns from the switcher step: an org switch resolving after the user closed the panel, or
  // opened the assistant over it, leaves things as the user left them.
  const showOrganisation = useCallback(() => {
    if (openPanelRef.current === 'switch-organisation') open('organisation');
  }, [open]);
  // Docked, the page beside the panel shows the change; covering it, the panel has to go for the change to be seen.
  const isDocked = useBreakpoints('lg');
  const closeIfCovering = useCallback(() => {
    if (!isDocked) close();
  }, [isDocked, close]);

  return {
    openPanel,
    shownPanel,
    isOpen: openPanel !== null,
    isDocked,
    onOpenChange,
    close,
    isOrganisationOpen,
    toggleAssistant,
    openAssistant,
    toggleOrganisation,
    showSwitcher,
    showOrganisation,
    closeIfCovering,
  };
};

export type PortalPanelsState = ReturnType<typeof usePortalPanels>;
